import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Group as GroupIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Chat as ChatIcon,
  Security as SecurityIcon,
  Book as BookIcon,
  Send as SendIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as LogoutIcon,
  Key as KeyIcon,
  Palette as PaletteIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { AppSidebar, Layout, StatusBadge, StyledCard } from './common';
import AppearanceSettings from './widget/AppearanceSettings';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Customer, WidgetConfig, DEFAULT_WIDGET_CONFIG, UserCreationRequest, DEFAULT_PERMISSIONS, ConversationSummary, ConversationDetail, CustomerStatsResponse } from '../services/api';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import * as pdfjsLib from 'pdfjs-dist';

type AdminMainSection = 'directory' | 'management';
type DirectoryRoleFilter = 'all' | 'admin' | 'customer';
type DirectoryStatusFilter = 'all' | 'active' | 'inactive';
type DirectorySortField = 'created_at' | 'name' | 'id' | 'role' | 'status';
type SortDirection = 'asc' | 'desc';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`management-tabpanel-${index}`}
      aria-labelledby={`management-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CustomerDashboard: React.FC = () => {
  const { logout, username, currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [activeMainSection, setActiveMainSection] = useState<AdminMainSection>('directory');
  const [activeManagementTab, setActiveManagementTab] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [contextText, setContextText] = useState('');
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>(DEFAULT_WIDGET_CONFIG);
  const [chatMessages, setChatMessages] = useState<{role: string, content: string}[]>([]);
  const [chatMessage, setChatMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [newUserData, setNewUserData] = useState<UserCreationRequest>({
    name: '',
    email: '',
    role: 'customer',
    permissions: []
  });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlsText, setUrlsText] = useState('');
  const drawerWidth = 280;

  // Conversations & Stats
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string>('');
  const [customerStats, setCustomerStats] = useState<CustomerStatsResponse | null>(null);
  const [convosLoading, setConvosLoading] = useState(false);
  const [convosError, setConvosError] = useState<string>('');
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string>('');
  const [conversationDetailLoading, setConversationDetailLoading] = useState(false);
  const [conversationDetail, setConversationDetail] = useState<ConversationDetail | null>(null);
  const [conversationLimit, setConversationLimit] = useState<number>(50);
  const [messageLimit, setMessageLimit] = useState<number>(50);

  // Directory controls
  const [directoryQuery, setDirectoryQuery] = useState('');
  const [directoryRoleFilter, setDirectoryRoleFilter] = useState<DirectoryRoleFilter>('all');
  const [directoryStatusFilter, setDirectoryStatusFilter] = useState<DirectoryStatusFilter>('all');
  const [directorySortField, setDirectorySortField] = useState<DirectorySortField>('created_at');
  const [directorySortDirection, setDirectorySortDirection] = useState<SortDirection>('desc');

  // Configure PDF.js worker (served from public/ for reliability)
  // @ts-ignore
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const state = (location.state || {}) as any;
    if (state && (state.adminView === 'directory' || state.adminView === 'management')) {
      setActiveMainSection(state.adminView);
    }
    if (state && typeof state.managementTab === 'number') {
      setActiveManagementTab(state.managementTab);
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedCustomer) {
      setWidgetConfig(selectedCustomer.widget_config || DEFAULT_WIDGET_CONFIG);
      if (selectedCustomer.context_text !== undefined) {
        setContextText(selectedCustomer.context_text);
        setContextLoading(false);
      } else {
        fetchContextText();
      }
      (async () => {
        try {
          const ref = doc(db, 'customers', selectedCustomer.id);
          const snap = await getDoc(ref);
          const urls: string[] = (snap.exists() && (snap.data() as any).urls) || [];
          setUrlsText(urls.join(', '));
        } catch (e) {
          // ignore
        }
      })();
    }
  }, [selectedCustomer]);

  // Load conversations + stats when the Conversations tab is opened
  useEffect(() => {
    if (!selectedCustomer) return;
    // Conversations tab index = 3 (after Status, Knowledge, Widget)
    if (activeManagementTab !== 3) return;
    void refreshConversationsAndStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeManagementTab, selectedCustomer?.id]);

  const refreshConversationsAndStats = async () => {
    if (!selectedCustomer) return;
    setStatsError('');
    setConvosError('');
    setStatsLoading(true);
    setConvosLoading(true);
    try {
      const [stats, convos] = await Promise.all([
        apiService.getUserStats(selectedCustomer.id),
        apiService.getUserConversations(selectedCustomer.id, conversationLimit),
      ]);
      setCustomerStats(stats);
      setConversations(convos);
    } catch (e: any) {
      setStatsError('Failed to load stats');
      setConvosError('Failed to load conversations');
    } finally {
      setStatsLoading(false);
      setConvosLoading(false);
    }
  };

  const openConversation = async (conversationId: string) => {
    if (!selectedCustomer) return;
    setSelectedConversationId(conversationId);
    setConversationDetail(null);
    setConversationDetailLoading(true);
    try {
      const detail = await apiService.getUserConversation(selectedCustomer.id, conversationId, messageLimit);
      setConversationDetail(detail);
    } catch (e: any) {
      setConversationDetail(null);
    } finally {
      setConversationDetailLoading(false);
    }
  };

  const fetchCustomers = async (useCache: boolean = true) => {
    setLoading(true);
    try {
      const lightData = await apiService.getAllUsersLight(1, 100);
      setCustomers(lightData.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContextText = async () => {
    if (!selectedCustomer) return;
    
    setContextLoading(true);
    try {
      const context = await apiService.getUserContext(selectedCustomer.id);
      setContextText(context);
      setSelectedCustomer(prev => prev ? { ...prev, context_text: context } : null);
    } catch (error) {
      console.error('Error fetching context:', error);
      setContextText('');
    } finally {
      setContextLoading(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomerName.trim()) return;
    
    setLoading(true);
    try {
      await apiService.addNewUser(newCustomerName.trim());
      setNewCustomerName('');
      await fetchCustomers();
      setSuccessMessage(`Customer "${newCustomerName.trim()}" added successfully!`);
    } catch (error) {
      console.error('Error adding customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserData.name.trim()) {
      setAddUserError('Name is required');
      return;
    }

    if (newUserData.role === 'admin' && !newUserData.email?.trim()) {
      setAddUserError('Email is required for admin users');
      return;
    }

    setAddUserLoading(true);
    setAddUserError('');

    try {
      await apiService.createUser({
        ...newUserData,
        permissions: newUserData.permissions || DEFAULT_PERMISSIONS[newUserData.role]
      });
      
      setSuccessMessage(`${newUserData.role === 'admin' ? 'Admin' : 'Customer'} "${newUserData.name}" created successfully!`);
      await fetchCustomers();
      setShowAddUserModal(false);
      setNewUserData({
        name: '',
        email: '',
        role: 'customer',
        permissions: []
      });
    } catch (error: any) {
      setAddUserError(error.response?.data?.message || 'Failed to create user. Please try again.');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!selectedCustomer) return;
    
    try {
      await apiService.updateUserStatus(selectedCustomer.id, status);
      await fetchCustomers();
      const updatedCustomer = { ...selectedCustomer, status };
      setSelectedCustomer(updatedCustomer);
      setSuccessMessage(`Customer ${status === 'active' ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleSaveContext = async () => {
    if (!selectedCustomer) return;
    
    setContextLoading(true);
    try {
      await apiService.updateUserContext(selectedCustomer.id, contextText);
      setSelectedCustomer(prev => prev ? { ...prev, context_text: contextText } : null);
      setSuccessMessage('Context updated successfully!');
    } catch (error) {
      console.error('Error saving context:', error);
    } finally {
      setContextLoading(false);
    }
  };

  const handleSaveUrls = async () => {
    if (!selectedCustomer) return;
    try {
      const ref = doc(db, 'customers', selectedCustomer.id);
      const urls = urlsText
        .split(',')
        .map(u => u.trim())
        .filter(u => u.length > 0);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        await updateDoc(ref, { urls });
      } else {
        await setDoc(ref, { urls }, { merge: true });
      }
      setSuccessMessage('URLs saved successfully!');
    } catch (e) {
      console.error('Error saving URLs:', e);
    }
  };

  const extractTextFromPdf = async (arrayBuffer: ArrayBuffer): Promise<string> => {
    // @ts-ignore
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    // @ts-ignore
    const pdf = await loadingTask.promise;
    let fullText = '';
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      // @ts-ignore
      const page = await pdf.getPage(pageNum);
      // @ts-ignore
      const content = await page.getTextContent({ normalizeWhitespace: true });
      let pageText = '';
      const items: any[] = (content as any).items || [];
      for (const item of items) {
        const text = (item && typeof item === 'object' && 'str' in item) ? (item as any).str : '';
        const hasEOL = (item && typeof item === 'object' && 'hasEOL' in item) ? Boolean((item as any).hasEOL) : false;
        pageText += text + (hasEOL ? '\n' : ' ');
      }
      fullText += pageText.trim() + '\n\n';
    }
    return fullText.trim();
  };

  const handlePdfUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handlePdfFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    try {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      if (file.type !== 'application/pdf') {
        setSuccessMessage('Please upload a PDF file.');
        return;
      }
      setContextLoading(true);
      const arrayBuffer = await file.arrayBuffer();
      const text = await extractTextFromPdf(arrayBuffer);
      setContextText((prev) => `${prev}${prev ? '\n\n' : ''}---\nAppended from ${file.name}:\n\n${text}`);
      setSuccessMessage('PDF content appended. Remember to Save Context.');
    } catch (error) {
      console.error('Error extracting PDF:', error);
    } finally {
      setContextLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveWidgetConfig = async () => {
    if (!selectedCustomer) return;
    
    try {
      await apiService.updateUserWidgetConfig(selectedCustomer.id, widgetConfig);
      await fetchCustomers();
      setSelectedCustomer(prev => prev ? { ...prev, widget_config: widgetConfig } : null);
      setSuccessMessage('Widget configuration saved successfully!');
    } catch (error) {
      console.error('Error saving widget config:', error);
    }
  };

  const handleSendMessage = async () => {
    // 1. Check if there's a message or selected customer
    if (!selectedCustomer?.id || !chatMessage.trim()) return;

    // 2. Prepare the new user message object
    const userMessage = { role: 'user', content: chatMessage.trim() };
    
    // 3. Create the complete, updated conversation history
    const updatedMessages = [...chatMessages, userMessage];

    // 4. Update the UI immediately with the user's message and clear the input
    setChatMessages(updatedMessages);
    setChatMessage('');

    try {
      // 5. CRITICAL FIX: Send the entire history object to the API service
      const response = await apiService.testCustomerChat(
        selectedCustomer.id,
        { messages: updatedMessages } // Send the object { messages: [...] }
      );
      
      // 6. Create the AI's response message object
      const assistantMessage = { 
        role: 'assistant', 
        content: response.response 
      };
      
      // 7. Update the UI with the AI's response
      setChatMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('❌ [CustomerDashboard] Error sending message:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, there was an error processing your message.' 
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const generateScriptCode = () => {
    if (!selectedCustomer) return '';
    
    return `<!-- Add this before closing </body> tag -->
<script>
    window.chatWidgetConfig = {
        apiKey: "${selectedCustomer.api_key}", 
        apiEndpoint: '${widgetConfig.apiEndpoint}',
        title: '${widgetConfig.title}',
        subtitle: '${widgetConfig.subtitle}',
        primaryColor: '${widgetConfig.primaryColor}',
        secondaryColor: '${widgetConfig.secondaryColor}',
        welcomeMessage: \`${JSON.stringify(widgetConfig.welcomeMessage).slice(1, -1)}\`,
        temperature: ${widgetConfig.temperature},
        maxTokens: ${widgetConfig.max_output_tokens},
        model: '${widgetConfig.model}'
    };
</script>
<script src="https://cdn.jsdelivr.net/gh/MohammedRashad/jackbot-widget@0.5/widget.js"></script>`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const directoryResults = useMemo(() => {
    const q = directoryQuery.trim().toLowerCase();

    const filtered = customers.filter((c) => {
      const role = (c.role || 'customer') as 'admin' | 'customer';
      const status = (c.status || 'active') as 'active' | 'inactive';

      if (directoryRoleFilter !== 'all' && role !== directoryRoleFilter) return false;
      if (directoryStatusFilter !== 'all' && status !== directoryStatusFilter) return false;

      if (!q) return true;

      const id = (c.id || '').toLowerCase();
      const name = (c.name || '').toLowerCase();
      const email = (c.email || '').toLowerCase();
      return id.includes(q) || name.includes(q) || email.includes(q);
    });

    const getSortKey = (c: Customer): string | number => {
      switch (directorySortField) {
        case 'name':
          return (c.name || '').toLowerCase();
        case 'id':
          return (c.id || '').toLowerCase();
        case 'role':
          return (c.role || 'customer').toLowerCase();
        case 'status':
          return (c.status || 'active').toLowerCase();
        case 'created_at':
        default:
          return c.created_at ? new Date(c.created_at).getTime() : 0;
      }
    };

    const sorted = [...filtered].sort((a, b) => {
      const av = getSortKey(a);
      const bv = getSortKey(b);

      const dir = directorySortDirection === 'asc' ? 1 : -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });

    return sorted;
  }, [
    customers,
    directoryQuery,
    directoryRoleFilter,
    directoryStatusFilter,
    directorySortField,
    directorySortDirection,
  ]);

  const ensureManagementCustomerSelected = async () => {
    if (selectedCustomer || customers.length === 0) return;
    setSelectedCustomer(customers[0]);
    setWidgetConfig(customers[0].widget_config || DEFAULT_WIDGET_CONFIG);
    setContextLoading(true);
  };

  const goToDirectory = () => {
    setActiveMainSection('directory');
    setMobileNavOpen(false);
  };

  const goToManagement = async () => {
    setActiveMainSection('management');
    await ensureManagementCustomerSelected();
    setMobileNavOpen(false);
  };

  return (
    <Layout
      title="Jackbot Admin Dashboard"
      username={username || undefined}
      onLogout={logout}
      onProfile={() => navigate('/profile')}
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Success Message */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage('')}
        >
          <Alert
            onClose={() => setSuccessMessage('')}
            severity="success"
            variant="filled"
          >
            {successMessage}
          </Alert>
        </Snackbar>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
          <Box
            component="nav"
            sx={{
              width: { md: sidebarCollapsed ? 76 : drawerWidth },
              flexShrink: { md: 0 },
            }}
            aria-label="Admin navigation"
          >
            <AppSidebar
              title="Admin"
              drawerWidth={drawerWidth}
              collapsedWidth={76}
              collapsed={sidebarCollapsed}
              onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
              mobileOpen={mobileNavOpen}
              onMobileClose={() => setMobileNavOpen(false)}
              topItems={[
                {
                  key: 'directory',
                  label: 'Customer Directory',
                  icon: <GroupIcon />,
                  selected: activeMainSection === 'directory',
                  onClick: goToDirectory,
                },
                {
                  key: 'management',
                  label: 'Customer Management',
                  icon: <SettingsIcon />,
                  selected: activeMainSection === 'management',
                  onClick: () => {
                    void goToManagement();
                  },
                },
                {
                  key: 'add-user',
                  label: 'Add New User',
                  icon: <PersonAddIcon />,
                  selected: false,
                  onClick: () => {
                    setShowAddUserModal(true);
                    setMobileNavOpen(false);
                  },
                },
              ]}
              bottomItems={[
                {
                  key: 'profile',
                  label: 'Profile',
                  icon: <PersonIcon />,
                  selected: false,
                  onClick: () => navigate('/profile'),
                },
                {
                  key: 'logout',
                  label: 'Logout',
                  icon: <LogoutIcon />,
                  color: 'error',
                  onClick: logout,
                },
              ]}
            />
          </Box>

          {/* Main content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-start', mb: 2 }}>
              <Button variant="outlined" startIcon={<MenuIcon />} onClick={() => setMobileNavOpen(true)}>
                Menu
              </Button>
            </Box>


            {/* Customer Directory */}
            {activeMainSection === 'directory' && (
              <Paper sx={{ mb: 3 }}>
                {loading && customers.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : customers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No customers found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add your first customer to get started
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Directory Controls */}
                    <Box
                      sx={{
                        p: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}
                    >
                      {/* Row 1: Search */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        <TextField
                          size="small"
                          sx={{ flex: 1, minWidth: { xs: '100%', md: 360 } }}
                          value={directoryQuery}
                          onChange={(e) => setDirectoryQuery(e.target.value)}
                          placeholder="Search by name, email, or ID…"
                          label="Search"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'text.secondary' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      {/* Row 2: Filters + Sort */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          flexWrap: 'wrap',
                        }}
                      >
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                          <InputLabel>Role</InputLabel>
                          <Select
                            value={directoryRoleFilter}
                            label="Role"
                            onChange={(e) => setDirectoryRoleFilter(e.target.value as any)}
                          >
                            <MenuItem value="all">All roles</MenuItem>
                            <MenuItem value="customer">Customer</MenuItem>
                            <MenuItem value="admin">Admin</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={directoryStatusFilter}
                            label="Status"
                            onChange={(e) => setDirectoryStatusFilter(e.target.value as any)}
                          >
                            <MenuItem value="all">All statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 190 }}>
                          <InputLabel>Sort</InputLabel>
                          <Select
                            value={directorySortField}
                            label="Sort"
                            onChange={(e) => setDirectorySortField(e.target.value as any)}
                          >
                            <MenuItem value="created_at">Created</MenuItem>
                            <MenuItem value="name">Name</MenuItem>
                            <MenuItem value="id">ID</MenuItem>
                            <MenuItem value="role">Role</MenuItem>
                            <MenuItem value="status">Status</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <InputLabel>Order</InputLabel>
                          <Select
                            value={directorySortDirection}
                            label="Order"
                            onChange={(e) => setDirectorySortDirection(e.target.value as any)}
                          >
                            <MenuItem value="desc">Desc</MenuItem>
                            <MenuItem value="asc">Asc</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    <TableContainer>
                      <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>API Key</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {directoryResults.map((customer) => (
                          <TableRow
                            key={customer.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setActiveMainSection('management');
                              setActiveManagementTab(0);
                            }}
                          >
                            <TableCell>{customer.id}</TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {customer.name}
                                </Typography>
                                {customer.email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {customer.email}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={customer.role || 'customer'}
                                size="small"
                                color={customer.role === 'admin' ? 'primary' : 'default'}
                                icon={customer.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                              />
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={customer.status || 'active'} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {customer.api_key}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {customer.created_at ? formatDate(customer.created_at) : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Manage Customer">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedCustomer(customer);
                                    setActiveMainSection('management');
                                    setActiveManagementTab(0);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Paper>
            )}

            {/* Customer Management */}
            {activeMainSection === 'management' && (
              <Paper sx={{ mb: 3, p: 3 }}>
                {customers.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No customers available to manage
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add your first customer to get started
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Select Customer</InputLabel>
                      <Select
                        value={selectedCustomer?.id || ''}
                        label="Select Customer"
                        onChange={async (e) => {
                          const customerId = e.target.value;
                          if (!customerId) {
                            setSelectedCustomer(null);
                            setContextText('');
                            return;
                          }

                          setChatMessages([]);
                          setConfirmDelete(false);
                          setActiveManagementTab(0);
                          setContextLoading(true);

                          try {
                            const customer = await apiService.getCustomerById(customerId);
                            setSelectedCustomer(customer);
                            setWidgetConfig(customer.widget_config || DEFAULT_WIDGET_CONFIG);
                          } catch (error) {
                            console.error('Error fetching customer:', error);
                            const fallbackCustomer = customers.find(c => c.id === customerId);
                            if (fallbackCustomer) {
                              setSelectedCustomer(fallbackCustomer);
                              setWidgetConfig(fallbackCustomer.widget_config || DEFAULT_WIDGET_CONFIG);
                            }
                          }
                        }}
                      >
                        {customers.map((customer) => (
                          <MenuItem key={customer.id} value={customer.id}>
                            {customer.name} (ID: {customer.id})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedCustomer && (
                      <>
                        {/* Customer Info Card */}
                        <StyledCard sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" gutterBottom>
                                {selectedCustomer.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {selectedCustomer.id}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                API Key: {selectedCustomer.api_key}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
                              <StatusBadge status={selectedCustomer.status || 'active'} />
                            </Box>
                          </Box>
                        </StyledCard>

                        {/* Management Tabs */}
                        <Paper variant="outlined" sx={{ mb: 2 }}>
                          <Tabs
                            value={activeManagementTab}
                            onChange={(_, newValue) => setActiveManagementTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="customer management tabs"
                          >
                            <Tab icon={<SecurityIcon />} label="Status & Security" />
                            <Tab icon={<BookIcon />} label="Knowledge Base" />
                            <Tab icon={<PaletteIcon />} label="Widget Settings" />
                            <Tab icon={<ChatIcon />} label="Statistics " />
                            <Tab icon={<SendIcon />} label="Test Chat" />
                          </Tabs>
                        </Paper>

                        {/* Status & Security */}
                        <TabPanel value={activeManagementTab} index={0}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                            <Box sx={{ flex: 1 }}>
                              <StyledCard title="Customer Status">
                                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                  <Button
                                    variant={selectedCustomer.status === 'active' ? 'contained' : 'outlined'}
                                    color="success"
                                    onClick={() => handleStatusChange('active')}
                                  >
                                    Activate
                                  </Button>
                                  <Button
                                    variant={selectedCustomer.status === 'inactive' ? 'contained' : 'outlined'}
                                    color="error"
                                    onClick={() => handleStatusChange('inactive')}
                                  >
                                    Deactivate
                                  </Button>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  Current status: <StatusBadge status={selectedCustomer.status || 'active'} />
                                </Typography>
                              </StyledCard>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <StyledCard title="API Access">
                                <Typography variant="body2" gutterBottom>
                                  API Key
                                </Typography>
                                <TextField
                                  fullWidth
                                  value={selectedCustomer.api_key}
                                  InputProps={{
                                    readOnly: true,
                                    startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                  }}
                                  sx={{ mb: 2 }}
                                />
                                <Button variant="outlined" size="small">
                                  Regenerate API Key
                                </Button>
                              </StyledCard>
                            </Box>
                          </Box>
                        </TabPanel>

                        {/* Knowledge Base */}
                        <TabPanel value={activeManagementTab} index={1}>
                          <StyledCard
                            title={`Knowledge Base for ${selectedCustomer.name}`}
                            subtitle="This text will be used to provide context for AI responses"
                          >
                            <input
                              type="file"
                              accept="application/pdf"
                              ref={fileInputRef}
                              style={{ display: 'none' }}
                              onChange={handlePdfFileChange}
                            />
                            <TextField
                              fullWidth
                              multiline
                              rows={12}
                              value={contextText}
                              onChange={(e) => setContextText(e.target.value)}
                              placeholder={contextLoading ? "Loading context..." : "Enter context information for the AI..."}
                              disabled={contextLoading}
                              sx={{ mb: 2 }}
                            />
                            <TextField
                              fullWidth
                              label="Comma-separated URLs"
                              value={urlsText}
                              onChange={(e) => setUrlsText(e.target.value)}
                              placeholder="https://example.com/a, https://example.com/b"
                              sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                Character count: {contextText.length.toLocaleString()}
                                {selectedCustomer?.context_text !== undefined && selectedCustomer?.context_text !== contextText && (
                                  <Chip
                                    label="Unsaved changes"
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handleSaveUrls}
                                >
                                  Save URLs
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={handlePdfUploadClick}
                                  disabled={contextLoading}
                                  startIcon={<UploadFileIcon />}
                                >
                                  Upload PDF
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={fetchContextText}
                                  disabled={contextLoading}
                                  startIcon={<RefreshIcon />}
                                >
                                  Refresh
                                </Button>
                                <Button
                                  variant="contained"
                                  onClick={handleSaveContext}
                                  disabled={contextLoading}
                                  startIcon={contextLoading ? <CircularProgress size={16} /> : <AddIcon />}
                                >
                                  Save Context
                                </Button>
                              </Box>
                            </Box>
                          </StyledCard>
                        </TabPanel>

                        {/* Widget Settings */}
                        <TabPanel value={activeManagementTab} index={2}>
                          <>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                                <Box sx={{ flex: 1 }}>
                                  <StyledCard title="Basic Settings">
                                    <TextField
                                      fullWidth
                                      label="Widget Title"
                                      value={widgetConfig.title}
                                      onChange={(e) => setWidgetConfig({ ...widgetConfig, title: e.target.value })}
                                      sx={{ mb: 2 }}
                                    />
                                    <TextField
                                      fullWidth
                                      label="Widget Subtitle"
                                      value={widgetConfig.subtitle}
                                      onChange={(e) => setWidgetConfig({ ...widgetConfig, subtitle: e.target.value })}
                                      sx={{ mb: 2 }}
                                    />
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={3}
                                      label="Welcome Message"
                                      value={widgetConfig.welcomeMessage}
                                      onChange={(e) => setWidgetConfig({ ...widgetConfig, welcomeMessage: e.target.value })}
                                      sx={{ mb: 2 }}
                                    />
                                  </StyledCard>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <StyledCard title="Appearance">
                                    <AppearanceSettings
                                      primaryColor={widgetConfig.primaryColor}
                                      secondaryColor={widgetConfig.secondaryColor}
                                      onChange={(next) => setWidgetConfig(prev => ({ ...prev, ...next }))}
                                    />
                                  </StyledCard>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  variant="contained"
                                  onClick={handleSaveWidgetConfig}
                                  // startIcon={<AddIcon />}
                                >
                                  Save Widget Settings
                                </Button>
                              </Box>
                            </Box>

                            {/* Integration Code */}
                            <StyledCard title="Integration Code" sx={{ mt: 3 }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Copy and paste this code into your website before the closing &lt;/body&gt; tag:
                              </Typography>
                              <Paper
                                sx={{
                                  p: 2,
                                  bgcolor: 'grey.900',
                                  color: 'grey.100',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  overflow: 'auto'
                                }}
                              >
                                <pre>{generateScriptCode()}</pre>
                              </Paper>
                            </StyledCard>
                          </>
                        </TabPanel>

                        {/* Conversations */}
                        <TabPanel value={activeManagementTab} index={3}>
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
                            <Box sx={{ flex: 1, minWidth: 320 }}>
                              <StyledCard
                                title="Statistics"
                                subtitle="Basic usage stats from recorded conversations"
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                      label={statsLoading ? 'Loading…' : `User msgs: ${customerStats?.lifetime?.total_user_messages ?? 0}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={statsLoading ? 'Loading…' : `Assistant msgs: ${customerStats?.lifetime?.total_assistant_messages ?? 0}`}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={statsLoading ? 'Loading…' : `Convos touched: ${customerStats?.lifetime?.total_conversations_touched ?? 0}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={statsLoading ? 'Loading…' : `Active (24h): ${customerStats?.recent_scan?.active_conversations_last_24h ?? 0}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </Box>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<RefreshIcon />}
                                    onClick={refreshConversationsAndStats}
                                    disabled={!selectedCustomer}
                                  >
                                    Refresh
                                  </Button>
                                </Box>
                                {statsError && (
                                  <Alert severity="error" sx={{ mt: 2 }}>
                                    {statsError}
                                  </Alert>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                  Latest conversation update: {customerStats?.recent_scan?.latest_conversation_updated_at || 'n/a'}
                                </Typography>
                              </StyledCard>

                              <StyledCard
                                title="Conversations"
                                subtitle="Click a conversation to view the thread"
                                sx={{ mt: 3 }}
                              >
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                                  <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>List limit</InputLabel>
                                    <Select
                                      label="List limit"
                                      value={conversationLimit}
                                      onChange={(e) => setConversationLimit(Number(e.target.value))}
                                    >
                                      <MenuItem value={25}>25</MenuItem>
                                      <MenuItem value={50}>50</MenuItem>
                                      <MenuItem value={100}>100</MenuItem>
                                      <MenuItem value={200}>200</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>Message limit</InputLabel>
                                    <Select
                                      label="Message limit"
                                      value={messageLimit}
                                      onChange={(e) => setMessageLimit(Number(e.target.value))}
                                    >
                                      <MenuItem value={25}>25</MenuItem>
                                      <MenuItem value={50}>50</MenuItem>
                                      <MenuItem value={100}>100</MenuItem>
                                      <MenuItem value={200}>200</MenuItem>
                                    </Select>
                                  </FormControl>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={refreshConversationsAndStats}
                                    startIcon={<RefreshIcon />}
                                  >
                                    Reload list
                                  </Button>
                                </Box>

                                {convosError && (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    {convosError}
                                  </Alert>
                                )}

                                {convosLoading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                  </Box>
                                ) : conversations.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">
                                    No tracked conversations yet. Enable tracking and send a few messages.
                                  </Typography>
                                ) : (
                                  <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Updated</TableCell>
                                          <TableCell>IP</TableCell>
                                          <TableCell>Msgs</TableCell>
                                          <TableCell>Preview</TableCell>
                                          <TableCell align="right">Open</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {conversations.map((c) => {
                                          const isSelected = selectedConversationId === c.id;
                                          const msgs = (c.message_count_user ?? 0) + (c.message_count_assistant ?? 0);
                                          const preview = (c.last_user_message_preview || c.last_assistant_message_preview || '').trim();
                                          return (
                                            <TableRow key={c.id} hover selected={isSelected}>
                                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                {c.updated_at || '—'}
                                              </TableCell>
                                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                                {c.last_ip || '—'}
                                              </TableCell>
                                              <TableCell>
                                                <Chip size="small" label={msgs} />
                                              </TableCell>
                                              <TableCell sx={{ maxWidth: 380 }}>
                                                <Typography variant="body2" noWrap title={preview || ''}>
                                                  {preview || '—'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align="right">
                                                <Button
                                                  size="small"
                                                  variant={isSelected ? 'contained' : 'outlined'}
                                                  onClick={() => openConversation(c.id)}
                                                >
                                                  View
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                )}
                              </StyledCard>
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 320 }}>
                              <StyledCard
                                title={conversationDetail ? `Conversation ${conversationDetail.id}` : 'Conversation Viewer'}
                                subtitle={conversationDetail ? `Model: ${conversationDetail.model || 'n/a'} • Reservon tools: ${conversationDetail.used_reservon_tools ? 'yes' : 'no'}` : 'Select a conversation on the left'}
                              >
                                {conversationDetailLoading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                  </Box>
                                ) : !conversationDetail ? (
                                  <Typography variant="body2" color="text.secondary">
                                    No conversation selected.
                                  </Typography>
                                ) : (
                                  <Box sx={{ height: 520, overflow: 'auto', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    {(conversationDetail.messages || []).length === 0 ? (
                                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                        No messages found for this conversation.
                                      </Typography>
                                    ) : (
                                      (conversationDetail.messages || []).map((m) => (
                                        <Box
                                          key={m.id}
                                          sx={{
                                            mb: 1.5,
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: m.role === 'user' ? 'primary.light' : 'grey.100',
                                            color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                            maxWidth: '92%',
                                            ml: m.role === 'user' ? 'auto' : 0,
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
                                            {m.role} • {m.created_at || '—'}
                                            {m.role === 'user' && m.ip ? ` • ${m.ip}` : ''}
                                          </Typography>
                                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {m.content}
                                          </Typography>
                                        </Box>
                                      ))
                                    )}
                                  </Box>
                                )}
                              </StyledCard>
                            </Box>
                          </Box>
                        </TabPanel>

                        {/* Test Chat */}
                        <TabPanel value={activeManagementTab} index={4}>
                          <>
                            <StyledCard title="Test Chat Interface">
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  API Endpoint: {widgetConfig.apiEndpoint} | API Key: {selectedCustomer ? `${selectedCustomer.api_key.slice(0,4)}…${selectedCustomer.api_key.slice(-4)}` : 'n/a'} | Status: {selectedCustomer?.status || 'unknown'}
                                </Typography>
                                <Button
                                  variant="outlined"
                                  onClick={() => setSimOpen(true)}
                                  disabled={!selectedCustomer || selectedCustomer.status !== 'active'}
                                >
                                  Open Widget Simulator
                                </Button>
                              </Box>
                              <Box sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                                  {chatMessages.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                                      No messages yet. Start a conversation to test the AI.
                                    </Typography>
                                  ) : (
                                    chatMessages.map((message, index) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          mb: 2,
                                          p: 2,
                                          borderRadius: 2,
                                          bgcolor: message.role === 'user' ? 'primary.light' : 'grey.100',
                                          color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                          maxWidth: '80%',
                                          ml: message.role === 'user' ? 'auto' : 0,
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {message.content}
                                        </Typography>
                                      </Box>
                                    ))
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <TextField
                                    fullWidth
                                    placeholder="Type your message..."
                                    value={chatMessage}
                                    onChange={(e) => setChatMessage(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendMessage();
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="contained"
                                    onClick={handleSendMessage}
                                    disabled={!chatMessage.trim()}
                                    endIcon={<SendIcon />}
                                  >
                                    Send
                                  </Button>
                                </Box>
                              </Box>
                            </StyledCard>

                            <Dialog open={simOpen} onClose={() => setSimOpen(false)} fullWidth maxWidth="md">
                              <DialogTitle>Widget Simulator</DialogTitle>
                              <DialogContent dividers>
                                <iframe
                                  title="Widget Simulator"
                                  style={{ width: '100%', height: 600, border: '0' }}
                                  srcDoc={(function(){
                                    const cfg = widgetConfig;
                                    const apiKey = selectedCustomer ? selectedCustomer.api_key : 'your-api-key';
                                    return `<!doctype html><html><head><meta charset=\"utf-8\" /><meta name=\"viewport\" content=\"width=device-width, initial-scale=1\" /><style>html,body{height:100%;margin:0}#root{height:100%}</style></head><body><div id=\"root\"></div><script>window.chatWidgetConfig={apiKey:${JSON.stringify(apiKey)},apiEndpoint:${JSON.stringify(cfg.apiEndpoint)},title:${JSON.stringify(cfg.title)},subtitle:${JSON.stringify(cfg.subtitle)},primaryColor:${JSON.stringify(cfg.primaryColor)},secondaryColor:${JSON.stringify(cfg.secondaryColor)},welcomeMessage:${JSON.stringify(cfg.welcomeMessage)},temperature:${JSON.stringify(cfg.temperature)},maxTokens:${JSON.stringify(cfg.max_output_tokens)},model:${JSON.stringify(cfg.model)}};</script><script src=\"https://cdn.jsdelivr.net/gh/MohammedRashad/jackbot-widget@0.5/widget.js\"></script></body></html>`;
                                  })()}
                                />
                              </DialogContent>
                              <DialogActions>
                                <Button onClick={() => setSimOpen(false)}>Close</Button>
                              </DialogActions>
                            </Dialog>
                          </>
                        </TabPanel>
                      </>
                    )}
                  </>
                )}
              </Paper>
            )}
          </Box>
        </Box>

        {/* Advanced User Creation Modal */}
        <Dialog
          open={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create New User</DialogTitle>
          <DialogContent>
            {addUserError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {addUserError}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Name"
              value={newUserData.name}
              onChange={(e) => setNewUserData(prev => ({ ...prev, name: e.target.value }))}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
              margin="normal"
              required={newUserData.role === 'admin'}
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={newUserData.role}
                label="Role"
                onChange={(e) => setNewUserData(prev => ({ ...prev, role: e.target.value as 'customer' | 'admin' }))}
              >
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddUserModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              variant="contained"
              disabled={addUserLoading}
              startIcon={addUserLoading ? <CircularProgress size={16} /> : <AddIcon />}
            >
              Create User
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default CustomerDashboard; 